import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { useSesion } from '../auth/useSesion'

/**
 * Pantalla de acceso, en dos mitades.
 *
 * A la izquierda, el panel de marca: dice **de que va esto** antes de pedir
 * nada. La muestra que lleva dentro no es adorno, es un reparto con la forma
 * exacta que tiene en la aplicacion —cliente, ficha de Odoo, pedido, la hora del
 * reparto y el total—, con las mismas insignias de resolucion que se veran
 * luego. Quien entra por primera vez ya reconoce la pantalla.
 *
 * A la derecha, el formulario y nada mas. Es un `<form>` de verdad, no un boton
 * suelto: asi se envia con Enter desde cualquiera de los dos campos, que es como
 * la gente rellena un formulario de dos lineas.
 *
 * **En movil el panel de marca desaparece.** Ocupa media pantalla para no decir
 * nada que el usuario necesite en ese momento; lo que queda es el formulario con
 * el rotulo de la casa encima.
 *
 * La entrada esta escalonada con un retardo por elemento. Es lo unico animado de
 * la pantalla y se apaga entero con `prefers-reduced-motion`.
 */

/** Las seis secciones, que es lo que el panel promete. */
const SECCIONES = ['Logistica', 'Conversaciones', 'Leads', 'Avisos', 'Agendamiento', 'Calendario']

/**
 * La muestra del panel. Cada fila lleva el estado con el que llegaria a la
 * revision, y los tres colores son los mismos que usa `InsigniaMotivo`.
 */
const MUESTRA = [
  { campo: 'Cliente', valor: 'Bar Pepe', estado: 'bien' },
  { campo: 'Ficha Odoo', valor: 'BAR PEPE SL', estado: 'bien' },
  { campo: 'Pedido', valor: '4 x BONO 4 18.9L', estado: 'bien' },
  { campo: 'Reparto', valor: 'lunes 18/08 - 08:00', estado: 'ojo' },
  { campo: 'Total', valor: '31,82 EUR', estado: 'bien' },
] as const

const PUNTO: Record<string, string> = {
  bien: 'bg-positivo-punto',
  ojo: 'bg-atencion-anillo',
}

/** Retardo de entrada. Un indice por elemento, en el orden en que se leen. */
function entrada(indice: number): React.CSSProperties {
  return { animationDelay: `${80 + indice * 70}ms` }
}

// `bg-lienzo` y no `bg-superficie`: el formulario ya va sobre `superficie`, asi
// que un campo del mismo color desapareceria. El lienzo es la superficie mas
// hundida de las tres, y un campo de texto es justo eso, un hueco.
const CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-lienzo px-3 py-2.5 text-sm text-tinta outline-none transition-colors placeholder:text-tinta-apagada focus:border-foco focus:ring-2 focus:ring-foco-halo'

export function Login() {
  const { sesion, cargando, entrar } = useSesion()
  const [correo, setCorreo] = useState('')
  const [contrasena, setContrasena] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Ya identificado: no tiene sentido enseñar el formulario.
  if (!cargando && sesion) return <Navigate to="/logistica" replace />

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      await entrar(correo, contrasena)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se ha podido iniciar sesion')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-superficie lg:grid-cols-[1.05fr_1fr]">
      {/* ── Panel de marca ─────────────────────────────────────────────── */}
      {/* El formulario va sobre `superficie` y el panel sobre `marca`, que es
          mas honda: en oscuro lo que se eleva se aclara, asi que la mitad del
          formulario queda por encima y la division se lee sola. El filete de
          borde remata el corte, que si no queda difuso. */}
      <aside className="relative hidden overflow-hidden border-r border-marca-borde bg-marca lg:flex lg:flex-col lg:justify-between lg:px-14 lg:py-12">
        {/* Dos halos muy tenues que le quitan la planitud al fondo. Van detras
            de todo y no reciben eventos. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-32 -top-40 size-[34rem] rounded-full bg-marca-acento/[0.07] blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-48 -right-24 size-[30rem] rounded-full bg-marca-acento/[0.05] blur-3xl"
        />

        <p
          className="relative animate-[surgir_.5s_ease-out_both] text-sm font-semibold uppercase tracking-[0.2em] text-marca-acento motion-reduce:animate-none"
          style={entrada(0)}
        >
          Aguas de Castellon
        </p>

        <div className="relative flex flex-col gap-9">
          <p
            className="animate-[surgir_.5s_ease-out_both] font-mono text-xs uppercase tracking-[0.16em] text-marca-tinta-2 motion-reduce:animate-none"
            style={entrada(1)}
          >
            {SECCIONES.join('  ·  ')}
          </p>

          <h2
            className="max-w-[15ch] animate-[surgir_.5s_ease-out_both] text-balance text-5xl font-semibold leading-[1.06] tracking-tight text-marca-tinta motion-reduce:animate-none"
            style={entrada(2)}
          >
            Cada reparto, cuadrado antes de salir.
          </h2>

          {/* La muestra: un cliente con la forma que tiene en la revision. */}
          <div
            className="max-w-md animate-[surgir_.5s_ease-out_both] rounded-xl border border-marca-borde bg-marca-honda/70 p-5 motion-reduce:animate-none"
            style={entrada(3)}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-marca-tinta-2">
              Reparto - revision
            </p>
            <dl className="mt-4 flex flex-col gap-2.5">
              {MUESTRA.map((fila) => (
                <div key={fila.campo} className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={`h-4 w-1 shrink-0 rounded-full ${PUNTO[fila.estado]}`}
                  />
                  <dt className="w-28 shrink-0 text-sm text-marca-tinta-2">{fila.campo}</dt>
                  <dd className="min-w-0 truncate font-mono text-sm text-marca-tinta">
                    {fila.valor}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <p
          className="relative animate-[surgir_.5s_ease-out_both] text-sm text-marca-tinta-2 motion-reduce:animate-none"
          style={entrada(4)}
        >
          Panel de operaciones - Tactica Empresarial
        </p>
      </aside>

      {/* ── Formulario ─────────────────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-14">
        <div className="w-full max-w-sm">
          {/* Solo en movil, donde el panel de marca no esta: sin esto la
              pantalla empezaria directamente en "Correo". */}
          <p className="mb-8 text-sm font-semibold uppercase tracking-[0.18em] text-acento-tinta lg:hidden">
            Aguas de Castellon
          </p>

          <p className="font-mono text-xs uppercase tracking-[0.16em] text-tinta-apagada">Acceso</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-tinta">
            Entra a tu panel
          </h1>

          <form onSubmit={alEnviar} className="mt-9 flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="correo" className="text-sm font-medium text-tinta-2">
                Correo
              </label>
              <input
                id="correo"
                name="correo"
                type="email"
                autoComplete="username"
                required
                autoFocus
                placeholder="nombre@aguasdecastellon.es"
                value={correo}
                onChange={(evento) => setCorreo(evento.target.value)}
                className={CAMPO}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="contrasena" className="text-sm font-medium text-tinta-2">
                Contrasena
              </label>
              <input
                id="contrasena"
                name="contrasena"
                type="password"
                autoComplete="current-password"
                required
                value={contrasena}
                onChange={(evento) => setContrasena(evento.target.value)}
                className={CAMPO}
              />
            </div>

            {error ? (
              <p
                role="alert"
                className="rounded-lg border border-negativo-anillo bg-negativo-fondo px-3 py-2 text-sm text-negativo-tinta"
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={enviando}
              className="mt-1 w-full rounded-lg bg-acento px-3 py-2.5 text-sm font-semibold text-tinta-inversa transition-colors hover:bg-acento-fuerte disabled:cursor-not-allowed disabled:bg-desactivado disabled:text-desactivado-tinta"
            >
              {enviando ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          {/* No hay recuperacion de contrasena: las rotan los administradores
              con el script. Un enlace que no lleva a ninguna parte es peor que
              no tenerlo. */}
          <p className="mt-8 text-sm text-tinta-apagada">
            El acceso lo da el administrador del panel.
          </p>
        </div>
      </main>
    </div>
  )
}
